import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  withFilterDefaults,
  toggleFilterValue,
  clearVenueFilters,
  countActiveFilters,
  serializeVenueFilters,
  parseVenueFilters,
} from '../utils/venueFilters';

const STORAGE_PREFIX = 'vrc.venueFilters.';

function readPersisted(scopeKey) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(STORAGE_PREFIX + scopeKey);
    return raw ? parseVenueFilters(raw) : null;
  } catch {
    return null;
  }
}

/**
 * REQ-104: one venue filter state per scope (today 'tourPlanner'; Renee's per-tab map can pass
 * 'rider', 'merch', ...). `setFilters` is a plain React state setter, so existing
 * `setFilters((prev) => ...)` callers keep working.
 *
 * options: { initial, persist = false } (persist keeps the state in localStorage per scope).
 */
export function useVenueFilters(scopeKey = 'tourPlanner', { initial, persist = false } = {}) {
  const [filters, setFilters] = useState(() => withFilterDefaults({
    ...(initial || {}),
    ...((persist && readPersisted(scopeKey)) || {}),
  }));

  const firstRun = useRef(true);
  useEffect(() => {
    if (!persist) return;
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    try {
      window.localStorage?.setItem(STORAGE_PREFIX + scopeKey, serializeVenueFilters(filters));
    } catch {
      // Private mode or blocked storage: filters still work for this visit.
    }
  }, [filters, persist, scopeKey]);

  const toggle = useCallback((groupId, value) => {
    setFilters((prev) => toggleFilterValue(prev, groupId, value));
  }, []);

  const clear = useCallback((groupIds) => {
    setFilters((prev) => clearVenueFilters(prev, groupIds));
  }, []);

  const activeCount = useMemo(() => countActiveFilters(filters), [filters]);

  return { filters, setFilters, toggle, clear, activeCount };
}

/**
 * Draft copy for the filter drawer: picks change the draft (and the live count) and only reach
 * the real filters on apply, so the 20k-line Tour Planner re-renders once per apply.
 */
export function useVenueFilterDraft(filters, setFilters) {
  const [draft, setDraft] = useState(null);
  const draftRef = useRef(null);
  draftRef.current = draft;
  const begin = useCallback(() => setDraft(withFilterDefaults(filters)), [filters]);
  const discard = useCallback(() => setDraft(null), []);
  const apply = useCallback(() => {
    const current = draftRef.current;
    if (current) setFilters((prev) => ({ ...prev, ...current }));
    setDraft(null);
  }, [setFilters]);
  return { draft, setDraft, begin, discard, apply, isOpen: draft !== null };
}

export default useVenueFilters;
